import { UseFormReturn } from 'react-hook-form';
import { useLingui } from '@lingui/react/macro';
import { t, Trans } from '@lingui/macro';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  STATUS_OPTIONS,
  type TariffVersionFormData,
  type TariffVersionFormInput
} from '@/schemas/tariffValidationSchemas';
import { rappenToCHF, chfToRappen } from '@/lib/money';
import { useCurrency } from '@/hooks/useCurrency';

interface GroupTypeOption {
  value: string;
  label: string;
}

interface TariffBasicInfoSectionProps {
  // Three generics: the schema's `.default()`s make `z.input` != `z.output`, so
  // the form holds the INPUT shape and only a validated submit yields the output.
  form: UseFormReturn<TariffVersionFormInput, unknown, TariffVersionFormData>;
  isLoading?: boolean;
  isEditMode?: boolean;
  groupTypeOptions?: GroupTypeOption[];
}

export function TariffBasicInfoSection({
  form,
  isLoading = false,
  isEditMode = false,
  groupTypeOptions = [],
}: TariffBasicInfoSectionProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();

  // Enabled toggles gate their paired amount input (disabled when rounding is off).
  const parentsRoundingEnabled = form.watch('rounding_parents_enabled');
  const subsidyRoundingEnabled = form.watch('rounding_subsidy_enabled');

  // Fallback to STANDARD if no options are available
  const effectiveGroupTypeOptions = groupTypeOptions.length > 0 
    ? groupTypeOptions 
    : [{ value: 'STANDARD', label: i18n._(t`Standard`) }];

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._(t`Version Name`)}</FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={i18n._(t`e.g., 2024 Standard Rates`)}
                disabled={isLoading}
              />
            </FormControl>
            <FormDescription>
              {i18n._(t`A descriptive name for this tariff version`)}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{i18n._(t`Description`)}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                placeholder={i18n._(t`Optional description...`)}
                disabled={isLoading}
                rows={3}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-3 gap-x-4">
        <FormField
          control={form.control}
          name="group_type"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel><Trans id="common.group.type">Group Type</Trans></FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={isLoading || isEditMode}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n._(t`Select group type`)} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {effectiveGroupTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {i18n._(t`The group type this tariff applies to`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel>{i18n._(t({ id: "common.fields.status", message: "Status" }))}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={isLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n._(t`Select status`)} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_default"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel>{i18n._(t({ id: "tariffs.fields.isDefault", message: "Is Default" }))}</FormLabel>
              <Select
                onValueChange={(value) => field.onChange(value === 'true')}
                value={field.value ? 'true' : 'false'}
                disabled={isLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n._(t`Select`)} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="false">{i18n._(t({ id: "common.no", message: "No" }))}</SelectItem>
                  <SelectItem value="true">{i18n._(t({ id: "common.yes", message: "Yes" }))}</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                {i18n._(t`Whether this is the default tariff for the group type`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-3 gap-x-4">
        <FormField
          control={form.control}
          name="age_transition_months"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel>{i18n._(t`Age Transition Threshold (months)`)}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormDescription>
                {i18n._(t`Age in months when children transition between billing groups`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="age_transition_months_subsidised"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel>{i18n._(t`Age Transition Threshold (months) for Subsidised contracts`)}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormDescription>
                {i18n._(t`Threshold used when the contract is subsidised`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="age_transition_timing"
          render={({ field }) => (
            <FormItem className="grid grid-rows-subgrid row-span-4 gap-y-2 space-y-0">
              <FormLabel>{i18n._(t`Age Transition Timing`)}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={isLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n._(t`Select timing`)} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="FOLLOWING_MONTH">{i18n._(t`Following Month`)}</SelectItem>
                  <SelectItem value="ON_BIRTHDAY">{i18n._(t`On Birthday`)}</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                {i18n._(t`When the transition takes effect`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="space-y-1">
        <h3 className="text-sm font-medium">
          <Trans>Rounding</Trans>
        </h3>
        <p className="text-sm text-muted-foreground">
          {i18n._({ id: 'tariffs.rounding.description', message: 'How parent and subsidy amounts on invoices from this version are rounded. The step is in {currency} (e.g. 0.05 rounds to the nearest 0.05).', values: { currency } })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <FormField
          control={form.control}
          name="rounding_parents_enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>{i18n._(t`Round parent amounts`)}</FormLabel>
                <FormDescription>
                  {i18n._(t`Round the parent invoice total to the configured step`)}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isLoading}
                  aria-label={i18n._(t`Round parent amounts`)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rounding_parents_amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{i18n._({ id: 'tariffs.rounding.parentStep', message: 'Parent rounding step ({currency})', values: { currency } })}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min={0.01}
                  value={rappenToCHF(field.value ?? 0)}
                  onChange={(e) => {
                    const chf = parseFloat(e.target.value);
                    field.onChange(Number.isNaN(chf) ? 0 : chfToRappen(chf));
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={isLoading || !parentsRoundingEnabled}
                  aria-disabled={isLoading || !parentsRoundingEnabled}
                />
              </FormControl>
              <FormDescription>
                {i18n._({ id: 'tariffs.rounding.parentStepHint', message: 'Step in {currency} for parent amounts (e.g. 0.05)', values: { currency } })}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <FormField
          control={form.control}
          name="rounding_subsidy_enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>{i18n._(t`Round subsidy amounts`)}</FormLabel>
                <FormDescription>
                  {i18n._(t`Round the subsidy figures to the configured step`)}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isLoading}
                  aria-label={i18n._(t`Round subsidy amounts`)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="rounding_subsidy_amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{i18n._({ id: 'tariffs.rounding.subsidyStep', message: 'Subsidy rounding step ({currency})', values: { currency } })}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min={0.01}
                  value={rappenToCHF(field.value ?? 0)}
                  onChange={(e) => {
                    const chf = parseFloat(e.target.value);
                    field.onChange(Number.isNaN(chf) ? 0 : chfToRappen(chf));
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={isLoading || !subsidyRoundingEnabled}
                  aria-disabled={isLoading || !subsidyRoundingEnabled}
                />
              </FormControl>
              <FormDescription>
                {i18n._({ id: 'tariffs.rounding.subsidyStepHint', message: 'Step in {currency} for subsidy amounts (e.g. 0.05)', values: { currency } })}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

    </div>
  );
}